import { z } from 'zod';
import { apiFor } from '@/api/client';

/*
 * Runtime contract for the "Système U — Quiz — Bien manger" dashboard payload.
 *
 * Transcribed from the design (claude.ai design "Système U — Quiz — Bien manger",
 * boards "Dashboard — Overview" and "Winners & prizes") and the Survey Project
 * Passport. THIS SCHEMA IS THE PIPELINE'S CONTRACT: the backend must emit exactly
 * this shape.
 *
 * Conventions, different from the older dashboards on purpose:
 *   - every figure is a RAW NUMBER (counts, seconds); the UI formats them, so
 *     the pipeline never decides how a French thousands separator looks;
 *   - a figure the source cannot give yet is `null` and renders "—" — never 0,
 *     because 0 claims a measurement (time in app needs the analytics stream);
 *   - the campaign runs in SEASONS (`campaign.periods`, mngs periods p1, p2 …).
 *     Season 1: Tue 6 Oct → Sun 1 Nov 2026 (4 weeks, the last one 6 days);
 *     the game is closed 2–16 Nov; Season 2: Tue 17 Nov → Sun 13 Dec (4 weeks,
 *     the last one 6 days). A season switches at midnight Paris time; a day
 *     between the seasons belongs to no season and no week (it is still in
 *     `days`), and the campaign `totals` are the seasons' days;
 *   - a week is 7 days counted from its season's first day (Tuesday → Monday),
 *     the season's last week clipped to its end. Weeks are numbered 1…N across
 *     the whole campaign (the key); the UI labels them per season ("S2 · Week 1").
 *   - `totals` count a member once over the whole campaign, a season's
 *     `totals` once in that season, so a member active in both seasons is in
 *     both seasons' figures and once in the campaign's.
 */

const weekSchema = z.object({
  /** Campaign week, 1-based, continuous across seasons (Season 2 starts at week 5). */
  week: z.number().int().positive(),
  /** ISO dates of the week's first and last day (local time; 7 days from the season's start, the last week clipped). */
  start: z.string(),
  end: z.string(),
  /** Season the week belongs to (`campaign.periods[].id`). */
  periodId: z.string(),
  /** done = finished, live = running now, planned = not started yet. */
  status: z.enum(['done', 'live', 'planned']),
  /** Players who completed at least one quiz that week. */
  uniquePlayers: z.number().nullable(),
  /** Quizzes completed that week. */
  plays: z.number().nullable(),
  /** Lottery tickets issued that week. */
  ticketsIssued: z.number().nullable(),
  /** Seconds in the app that week, all users. Null without analytics events. */
  timeInAppSec: z.number().nullable(),
  /**
   * Share of the week's unique players by quizzes played that week, in %
   * (0–100). Index 0 = 1 play … last index = the weekly maximum. Empty for a
   * week that has not started.
   */
  playsDistributionPct: z.array(z.number()),
  /**
   * Quiz sessions OPENED that week — each uses an attempt, finished or not
   * (mngs `spend_game` daily counters). Not completed = this − `plays`.
   * Null when not measured. Defaulted so a payload from before the field
   * existed still parses.
   */
  quizzesStarted: z.number().nullable().default(null),
  /** Players who opened at least one quiz that week. */
  playersStarted: z.number().nullable().default(null),
});

/** One local campaign day (Europe/Paris). Both figures are null for a day still to come. */
const daySchema = z.object({
  /** ISO date, YYYY-MM-DD. */
  date: z.string(),
  /** Members who visited that day — a visitor back on another day counts again on that day. */
  uniqueVisitors: z.number().nullable(),
  /** Surveys submitted that day = quizzes completed (the daily share of `plays`). */
  plays: z.number().nullable(),
});

const consentsSchema = z.object({
  /** Members with at least one marketing consent (email or SMS) given. */
  total: z.number(),
  emailOnly: z.number(),
  emailAndSms: z.number(),
  smsOnly: z.number(),
  /** Members with an email consent (= emailOnly + emailAndSms). */
  email: z.number(),
  /** Members with an SMS consent (= smsOnly + emailAndSms). */
  sms: z.number(),
});

/**
 * Figures over a stretch of the campaign: the whole campaign (`totals`) or one
 * season (`campaign.periods[].totals`). Members are distinct within the
 * stretch. Consents of a season are the decisions taken in it; the campaign's
 * are each member's latest decision.
 */
const figuresSchema = z.object({
  uniqueVisitors: z.number(),
  /** Members who accepted the T&Cs. */
  optIns: z.number(),
  /** Members who completed at least one quiz. */
  uniquePlayers: z.number(),
  /** Quizzes completed. */
  plays: z.number(),
  ticketsIssued: z.number(),
  /** Seconds in the app, all users. The UI derives "per user" (÷ unique players). */
  timeInAppSec: z.number().nullable(),
  consents: consentsSchema,
  /** Quiz sessions opened, finished or not. Null when the daily counters are not available. */
  quizzesStarted: z.number().nullable().default(null),
  /** Players who opened at least one quiz (≥ uniquePlayers, who finished one). */
  playersStarted: z.number().nullable().default(null),
});

/** A season of the campaign (an mngs period: p1 = Season 1). */
const periodSchema = z.object({
  id: z.string(),
  /** "Season 1" — the UI writes its own label, in the page language. */
  label: z.string(),
  /** The season's theme as the client names it ("Les Fruits et Légumes d'Automne"). */
  theme: z.string().nullable().optional(),
  /** Campaign weeks in the season, in order. */
  weeks: z.array(z.number().int().positive()),
  /** First and last local day of the season. Null in a payload from before seasons: the UI takes its weeks' dates. */
  start: z.string().nullable().default(null),
  end: z.string().nullable().default(null),
  /**
   * The season's own figures. Null in a payload from before seasons: the UI
   * then sums what adds up over weeks and shows "—" for the distinct counts.
   */
  totals: figuresSchema.nullable().default(null),
});

export const surveyPayloadSchema = z.object({
  campaign: z.object({
    name: z.string(),
    /** ISO dates, local campaign time. */
    start: z.string(),
    end: z.string(),
    timezone: z.string(),
    /** True while the campaign is running — drives the "Live" pill. */
    live: z.boolean(),
    /** Campaign week running now (1-based); 0 before go-live. */
    currentWeek: z.number().int().min(0),
    totalWeeks: z.number().int().positive(),
    /** Quizzes a player may complete per week (passport mg10: 3). */
    weeklyPlayLimit: z.number().int().positive(),
    /** The seasons, in order. */
    periods: z.array(periodSchema),
  }),
  /** Campaign to date, a member counted once whatever the seasons they took part in. */
  totals: figuresSchema,
  /** Every campaign week, including the ones still to come. */
  weeks: z.array(weekSchema),
  /** Every campaign day, go-live → end. Defaulted so a payload from before the field existed still parses. */
  days: z.array(daySchema).default([]),
});

export const surveyEnvelopeSchema = z.object({
  data: surveyPayloadSchema,
  meta: z.object({ last_refreshed_at: z.string().nullable() }).partial().optional(),
});

export type SurveyPayload = z.infer<typeof surveyPayloadSchema>;
export type SurveyWeek = z.infer<typeof weekSchema>;
export type SurveyPeriod = z.infer<typeof periodSchema>;
export type SurveyDay = z.infer<typeof daySchema>;
export type SurveyFigures = z.infer<typeof figuresSchema>;
export type SurveyConsents = z.infer<typeof consentsSchema>;
export type SurveyEnvelope = z.infer<typeof surveyEnvelopeSchema>;

/** Same generic report endpoint every dashboard uses: GET <base>/reports/<id>. */
export async function getSurveyReport(reportId: string, baseUrl: string): Promise<SurveyEnvelope> {
  const raw = await apiFor(baseUrl).get(`reports/${reportId}`).json();
  return surveyEnvelopeSchema.parse(raw);
}
